import type { AgentSkill } from "@common/schemas/domains/agentSkills/agent-skill.schema";
import { AgentSkillKind } from "@common/types/agentSkill";
import { Link } from "@design-system/Link/Link";
import { HStack } from "@design-system/HStack/HStack";
import { Tag } from "@design-system/Tag/Tag";
import { VStack } from "@design-system/VStack/VStack";
import { StateAwareLink } from "@components/routes/StateAwareLink";
import {
    AGENT_SKILL_CATEGORY_LABELS,
    AGENT_SKILL_LOAD_MODE_LABELS,
    FIRM_SCOPE_LABEL,
    KICK_AUTHOR_LABEL,
    SUMMARY_PENDING_MESSAGE,
} from "./agentSkillCopy";
import styles from "./AgentSkills.module.scss";

interface Props {
    skill: AgentSkill;
    clientName: string | null;
}

// Consumers without a label are not shown; agent chat is behind a feature flag.
const CONSUMER_LABELS: Partial<
    Record<
        AgentSkill["summary"] extends infer S
            ? S extends { consumers: Array<infer C> }
                ? C extends string
                    ? C
                    : never
                : never
            : never,
        string
    >
> = {
    close_run: "Agent",
    mcp: "MCP",
};

const DetailsRow = ({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) => (
    <div className={styles.detailsRow}>
        <span className="text-secondary">{label}</span>
        {children}
    </div>
);

const TagList = ({ items }: { items: string[] }) => (
    <HStack gap={4} wrap>
        {items.map((item) => (
            <Tag key={item} color="neutral" size="sm" outline>
                {item}
            </Tag>
        ))}
    </HStack>
);

export const AgentSkillDetailsTab = ({ skill, clientName }: Props) => {
    const isKick = skill.kind === AgentSkillKind.KICK;
    const { summary } = skill;
    const consumerLabels = (summary?.consumers ?? []).flatMap((consumer) => {
        const label = CONSUMER_LABELS[consumer];
        return label === undefined ? [] : [label];
    });
    return (
        <VStack gap={8}>
            <DetailsRow label="Source">
                <Tag color="neutral" size="sm" outline>
                    {isKick ? KICK_AUTHOR_LABEL : "Firm"}
                </Tag>
            </DetailsRow>
            <DetailsRow label="Scope">
                <Tag color="neutral" size="sm" outline>
                    {clientName ?? FIRM_SCOPE_LABEL}
                </Tag>
            </DetailsRow>
            <DetailsRow label="State">
                <Tag color="neutral" size="sm">
                    {skill.isEnabled ? "Enabled" : "Disabled"}
                </Tag>
            </DetailsRow>
            <DetailsRow label="Version">
                <span>{skill.currentVersion}</span>
            </DetailsRow>
            <DetailsRow label="Title">
                <span>{skill.title}</span>
            </DetailsRow>
            <DetailsRow label="Description">
                <span>{skill.description}</span>
            </DetailsRow>
            <DetailsRow label="Category">
                <span>{AGENT_SKILL_CATEGORY_LABELS[skill.category]}</span>
            </DetailsRow>
            <DetailsRow label="Frequency">
                <span>{AGENT_SKILL_LOAD_MODE_LABELS[skill.loadMode]}</span>
            </DetailsRow>
            {isKick && (
                <DetailsRow label="Tools">
                    <TagList items={skill.tools} />
                </DetailsRow>
            )}
            <h3 className={styles.detailsHeading}>Summary</h3>
            {summary === null ? (
                <span className="text-secondary">
                    {SUMMARY_PENDING_MESSAGE}
                </span>
            ) : (
                <>
                    <DetailsRow label="Purpose">
                        <span>{summary.purpose}</span>
                    </DetailsRow>
                    {summary.usage !== "" && (
                        <DetailsRow label="When to use">
                            <span>{summary.usage}</span>
                        </DetailsRow>
                    )}
                    <DetailsRow label="Tools">
                        <TagList items={summary.tools} />
                    </DetailsRow>
                    {summary.relatedSkills.length > 0 && (
                        <DetailsRow label="Related skills">
                            <HStack gap={8} wrap>
                                {summary.relatedSkills.map((name) => (
                                    <Link
                                        key={name}
                                        render={
                                            <StateAwareLink
                                                to={`/automation/skills?search=${encodeURIComponent(name.replace(/^kick\//, ""))}`}
                                            />
                                        }
                                    >
                                        {name}
                                    </Link>
                                ))}
                            </HStack>
                        </DetailsRow>
                    )}
                    {consumerLabels.length > 0 && (
                        <DetailsRow label="Used by">
                            <TagList items={consumerLabels} />
                        </DetailsRow>
                    )}
                </>
            )}
        </VStack>
    );
};
